import React, { useState, useEffect, useRef } from "react";
import { ReactComponent as ArrowDown } from "../../../assets/images/icon-arrow-down.svg";
import { ReactComponent as ArrowUp } from "../../../assets/images/icon-arrow-up.svg";
import useCustomStyles from "../../../hooks/useCustomStyles";
import { dropdownStyles } from "../../../assets/styles/ClientsStyles/dropdownStyles";
import { useSelector } from "react-redux";
import { selectState } from "../../../store/slices/themeSlice";

interface Option {
  key: string;
  value: string | number;
  displayName?: string; // Optional display name for better UX
}

interface LabeledDropdownProps {
  /**
   * Array of options to display in the dropdown.
   */
  options: Option[];

  /**
   * The initial label to show in the dropdown button.
   */
  initialState: string | number;

  /**
   * Callback when an option is selected.
   */
  handleSelect: (value: string | number) => void;

  /**
   * Optional label displayed above the dropdown.
   */
  label?: string;
  /**
   * If true, shows the label and dropdown side-by-side (inline).
   */
  isHorizontal?: boolean;
  /**
   * If true, indicates the dropdown is part of a group (two in one row).
   */
  isGrouped?: boolean;
}

/**
 * LabeledDropdown is a reusable component that displays a labeled dropdown menu.
 * It shows a button with a selected value, and toggles a list of options.
 * Clicking an option selects it and calls the handleSelect callback.
 */
const LabeledDropdown: React.FC<LabeledDropdownProps> = ({
  options,
  handleSelect,
  initialState,
  label,
  isHorizontal,
  isGrouped,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedOption, setSelectedOption] = useState(initialState);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { theme } = useSelector(selectState);
  const classes = useCustomStyles(dropdownStyles, theme);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleOptionSelect = (item: Option) => {
    setSelectedOption(item.value);
    handleSelect(item.value);
    setShowDropdown(false);
  };

  // Helper function to get display text for a value
  const getDisplayText = (value: string | number): string => {
    const option = options.find((opt) => opt.value === value);
    return option?.displayName || option?.value?.toString() || value.toString();
  };

  return (
    // <div
    //   ref={dropdownRef}
    //   className={classes.rootLabelDropDown}
    //   style={{
    //     display: isHorizontal ? "flex" : "block",
    //     alignItems: isHorizontal ? "center" : "initial",
    //     gap: isHorizontal ? "12px" : undefined,
    //     alignContent: "center",
    //     marginBottom: "16px",
    //   }}
    // >
    <div
      ref={dropdownRef}
      className={classes.rootLabelDropDown}
      style={{
        display: isHorizontal ? "flex" : "block",
        alignItems: isHorizontal ? "center" : "initial",
        gap: isHorizontal ? "12px" : undefined,
        alignContent: "center",
        marginBottom: "16px",
        // background: isGrouped ? "#F6F6F6" : "transparent",
        borderRadius: isGrouped ? "8px" : undefined,
        padding: isGrouped ? "8px" : undefined,
      }}
    >
      {label && (
        <label
          className={classes.label}
          style={{
            width: isHorizontal ? "20%" : "100%",
            whiteSpace: isHorizontal ? "nowrap" : "normal",
          }}
        >
          {label}
        </label>
      )}

      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className={`${classes.buttonLabelDropDown} ${
          showDropdown ? "open" : ""
        }`}
        style={{
          background: isHorizontal || isGrouped ? "#F6F6F6" : "#fff",
          maxWidth: isHorizontal ? "90%" : "100%",
        }}
      >
        <span className={classes.buttonText}>
          {getDisplayText(selectedOption)}
        </span>
        <span className={classes.iconContainer}>
          {showDropdown ? (
            <ArrowUp width={15} height={15} />
          ) : (
            <ArrowDown width={15} height={15} />
          )}
        </span>
      </button>

      {showDropdown && options.length > 0 && (
        <div className={classes.dropdownMenuLabelDropDown}>
          {options.map((item) => (
            <div
              key={item.key}
              className={classes.dropdownItem}
              onClick={() => handleOptionSelect(item)}
            >
              {item.displayName || item.value}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LabeledDropdown;
